import { Injectable, Type } from "@angular/core";
import { DialogService, DynamicDialogConfig } from "primeng/dynamicdialog";
import { ConfirmationDialogComponent } from "../../components/confirmation-dialog/confirmation-dialog.component";
import { ConfirmationPopupInterface } from "../../interfaces/confirmation-popup";
import { Subscription } from "rxjs";
import { AlertPopupInterface } from "../../interfaces/alert-popup";
import { AlertDialogComponent } from "../../components/alert-dialog/alert-dialog.component";
import { TranslateService } from "@ngx-translate/core";

@Injectable({
  providedIn: "root",
})
export class PopupService {
  private subscription!: Subscription;
  constructor(
    private dialogService: DialogService,
    private translateService: TranslateService
  ) {}

  /**
   * @param confirmationOptions
   * */
  confirm(confirmationOptions?: ConfirmationPopupInterface): Promise<boolean> {
    // Default Options
    const options = {
      header: "areYouSure?",
      message: "",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      width: "450px",
      height: "auto",
      ...confirmationOptions,
    } as ConfirmationPopupInterface;
    return this.open<boolean>(
      {
        data: {
          message: options.message,
          acceptLabel: options.acceptLabel,
          rejectLabel: options.rejectLabel,
          acceptLabelColor: options.acceptLabelColor,
          outlined: options.outlined,
        },
        header: options.header,
        width: options.width,
        height: options.height,
        modal: true,
        dismissableMask: true,
      },
      ConfirmationDialogComponent
    );
  }

  getDynamicDialogRef(dialogOptions: DynamicDialogConfig, dialogComponent: Type<unknown>, dt?: Record<string, unknown>) {
    if (dialogOptions.header) {
      dialogOptions.header = this.translateService.instant(dialogOptions.header);
    }
    const ref = this.dialogService.open(dialogComponent, dialogOptions);
    this.dialogService.getInstance(ref).dt = dt;
    return ref;
  }

  /**
   * @param dialogOptions options for the selected component
   * @param dialogComponent The dialog component to be called
   * @param dt To control the custom style tokens in dynamic dialog
   */
  open<T>(dialogOptions: DynamicDialogConfig, dialogComponent: Type<unknown>, dt?: Record<string, unknown>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.subscription = this.getDynamicDialogRef(dialogOptions, dialogComponent, dt).onClose.subscribe({
        next: value => {
          this.subscription.unsubscribe();
          return resolve(value);
        },
        error: (err: any) => {
          this.subscription.unsubscribe();
          return reject(err);
        },
        complete: () => {
          this.subscription.unsubscribe();
        },
      });
    });
  }

  flatOpen(dialogOptions: DynamicDialogConfig, dialogComponent: Type<unknown>) {
    return this.dialogService.open(dialogComponent, {
      focusOnShow: false,
      modal: true,
      dismissableMask: false,
      ...dialogOptions,
    });
  }

  alert(alertOptions?: AlertPopupInterface): Promise<boolean> {
    // Default Options
    const options = {
      header: "ok",
      width: "50%",
      height: "auto",
      label: "ok",
      ...alertOptions,
    } as AlertPopupInterface;
    return this.open(
      {
        data: {
          label: options.label,
          message: options?.message,
        },
        header: options.header,
        width: options.width,
        height: options.height,
      },
      AlertDialogComponent
    );
  }
}
